import { createSelector } from 'reselect';
import { find, pathOr, propEq } from 'ramda';
import { MetadataConstants, MetadataSelectors, SessionSelectors } from 'invision-core';
import { MetadataCodeSelector } from 'invision-core/src/components/metadata/codes/codes.selectors';
import { CODES } from 'invision-core/src/components/metadata/codes/codes.constants';
import { EMPTY_ARRAY } from '../constants/common.constants';
import { SelectedCustomerSelector } from './customer.selectors';
import { pageNumberSelector } from './order.history.filter.selectors';
import i18n from 'invision-core/src/components/i18n/i18n';
import { getBoolOrDefault } from 'invision-core/src/components/helpers/bool.helper';
import CustomerCareKeys from '../../locales/keys';
import { createImmutableSelector } from 'invision-core/src/utilities/create.immutable.selector';
var hasOrderHistory = function hasOrderHistory(selectedCustomer) {
  return selectedCustomer && selectedCustomer.orderHistory;
};
export var ReasonCodesSelector = createImmutableSelector([MetadataCodeSelector(CODES.ReasonCodes)], function (serviceReasonCodes) {
  return serviceReasonCodes && serviceReasonCodes.items ? serviceReasonCodes.items : EMPTY_ARRAY;
});
export var IsFetchingOrdersSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return hasOrderHistory(selectedCustomer) ? selectedCustomer.orderHistory.isFetchingOrders : null;
});
export var TotalPagesSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return hasOrderHistory(selectedCustomer) ? selectedCustomer.orderHistory.pageCount : null;
});
export var RecordCountSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return hasOrderHistory(selectedCustomer) ? selectedCustomer.orderHistory.recordCount : null;
});
export var StartRecordSelector = createSelector([pageNumberSelector, SessionSelectors.PageSizePreferenceSelector], function (pageNumber, pageSize) {
  return (pageNumber - 1) * pageSize + 1;
});
export var EndRecordSelector = createSelector([RecordCountSelector, pageNumberSelector, SessionSelectors.PageSizePreferenceSelector], function (recordCount, pageNumber, pageSize) {
  var endNumber = pageNumber * pageSize;
  if (endNumber < recordCount) {
    return endNumber;
  }
  return recordCount;
});
export var OrdersSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return hasOrderHistory(selectedCustomer) ? selectedCustomer.orderHistory.data : null;
});
export var IsRetrievingInventory = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return hasOrderHistory(selectedCustomer) ? selectedCustomer.orderHistory.isRetrievingInventory : null;
});
export var RetrieveInventoryError = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return hasOrderHistory(selectedCustomer) ? selectedCustomer.orderHistory.retrieveInventoryError : null;
});
export var TableDataSelector = createSelector([OrdersSelector, ReasonCodesSelector, MetadataSelectors.codes.MetadataCodeTypeSelector(MetadataConstants.codes.OrderType)], function (orders, reasonCodes, orderTypes) {
  if (orders && orderTypes.length) {
    return orders.map(function (order) {
      var orderTypeEval = propEq(order.Type.toString(), 'Value');
      var orderType = order.TransferDetail ? {
        Name: i18n.translate(CustomerCareKeys.ORDER_DETAILS.TRANSFER_ORDER_TYPE)
      } : find(orderTypeEval, orderTypes);
      var reason = (reasonCodes || []).find(function (reasonCode) {
        return order.ReasonCode && reasonCode.Value === order.ReasonCode.toString();
      });
      return order.set('OrderTypeName', orderType.Name).set('OrderReasonValue', reason ? reason.Name : null).set('OrderName', order.OrderDescription);
    });
  }
  return [];
});
export var IsCancellingOrderSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return pathOr(false, ['orderHistory', 'isCancellingOrder'], selectedCustomer);
});
export var CancelOrderErrorSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return pathOr(false, ['orderHistory', 'cancelOrderError'], selectedCustomer);
});
export var GroupOrderFeatureVisibilitySelector = createSelector([MetadataSelectors.codes.MetadataCodeTypeSelector(CODES.FeatureToggleConfig)], function (feaureToggleCodes) {
  if (feaureToggleCodes !== null && feaureToggleCodes !== void 0 && feaureToggleCodes.length) {
    var _findAdditonalPropert, _groupOrderingFeature, _findAdditonalPropert2, _groupOrderingFeature2;
    var groupOrderingFeatureCodes = feaureToggleCodes.filter(function (featureToggleCode) {
      return findAdditonalProperty(featureToggleCode === null || featureToggleCode === void 0 ? void 0 : featureToggleCode.AdditionalProperties, 'GroupOrdering', 'Value');
    });
    var isGroupOrderingFeatureEnabled = ((_findAdditonalPropert = findAdditonalProperty(groupOrderingFeatureCodes === null || groupOrderingFeatureCodes === void 0 ? void 0 : (_groupOrderingFeature = groupOrderingFeatureCodes.find(function (groupOrderingFeatureCode) {
      return !groupOrderingFeatureCode.hasOwnProperty('Global');
    })) === null || _groupOrderingFeature === void 0 ? void 0 : _groupOrderingFeature.AdditionalProperties, 'enabled')) === null || _findAdditonalPropert === void 0 ? void 0 : _findAdditonalPropert.Value) === 'True' ? true : false;
    var isGroupOrderingFeatureGlobalValue = ((_findAdditonalPropert2 = findAdditonalProperty(groupOrderingFeatureCodes === null || groupOrderingFeatureCodes === void 0 ? void 0 : (_groupOrderingFeature2 = groupOrderingFeatureCodes.find(function (groupOrderingFeatureCode) {
      return groupOrderingFeatureCode.hasOwnProperty('Global');
    })) === null || _groupOrderingFeature2 === void 0 ? void 0 : _groupOrderingFeature2.AdditionalProperties, 'enabled')) === null || _findAdditonalPropert2 === void 0 ? void 0 : _findAdditonalPropert2.Value) === 'True' ? true : false;
    return isGroupOrderingFeatureEnabled || isGroupOrderingFeatureGlobalValue;
  }
  return false;
});
export var GranularSecurityAccessFeatureVisibilitySelector = createSelector([MetadataSelectors.codes.MetadataCodeTypeSelector(CODES.FeatureToggleConfig)], function (feaureToggleCodes) {
  if (feaureToggleCodes !== null && feaureToggleCodes !== void 0 && feaureToggleCodes.length) {
    var _findAdditonalPropert3, _granularSecurityFeat, _findAdditonalPropert4, _granularSecurityFeat2;
    var granularSecurityFeatureCodes = feaureToggleCodes.filter(function (featureToggleCode) {
      return findAdditonalProperty(featureToggleCode === null || featureToggleCode === void 0 ? void 0 : featureToggleCode.AdditionalProperties, 'CustomerCareGranularSecurityAccess', 'Value');
    });
    var isGranularSecurityFeatureEnabled = ((_findAdditonalPropert3 = findAdditonalProperty(granularSecurityFeatureCodes === null || granularSecurityFeatureCodes === void 0 ? void 0 : (_granularSecurityFeat = granularSecurityFeatureCodes.find(function (granularSecurityFeatureCode) {
      return !granularSecurityFeatureCode.hasOwnProperty('Global');
    })) === null || _granularSecurityFeat === void 0 ? void 0 : _granularSecurityFeat.AdditionalProperties, 'enabled')) === null || _findAdditonalPropert3 === void 0 ? void 0 : _findAdditonalPropert3.Value) === 'True' ? true : false;
    var isGranularSecurityFeatureGlobalValue = ((_findAdditonalPropert4 = findAdditonalProperty(granularSecurityFeatureCodes === null || granularSecurityFeatureCodes === void 0 ? void 0 : (_granularSecurityFeat2 = granularSecurityFeatureCodes.find(function (granularSecurityFeatureCode) {
      return granularSecurityFeatureCode.hasOwnProperty('Global');
    })) === null || _granularSecurityFeat2 === void 0 ? void 0 : _granularSecurityFeat2.AdditionalProperties, 'enabled')) === null || _findAdditonalPropert4 === void 0 ? void 0 : _findAdditonalPropert4.Value) === 'True' ? true : false;
    return isGranularSecurityFeatureEnabled || isGranularSecurityFeatureGlobalValue;
  }
  return false;
});
function findAdditonalProperty(additionalProperties, targetProperty) {
  var type = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'Key';
  var findProperty = function findProperty(property) {
    return property[type] === targetProperty;
  };
  return additionalProperties === null || additionalProperties === void 0 ? void 0 : additionalProperties.find(findProperty);
}